import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { TickerItem } from "./types";

interface UseTickersFilterReturn {
  readonly allSelected: boolean;
  readonly selectedTickers: Set<string>;
  readonly activeTickers: string[];
  readonly toggleTicker: (ticker: string) => void;
}

// Manages ticker selection state for filtering watchlist news.
// "All Tickers" is represented by an empty selection set (no explicit boolean).
export function useTickersFilter(
  tickers: readonly TickerItem[]
): UseTickersFilterReturn {
  const [selectedTickers, setSelectedTickers] = useState<Set<string>>(
    new Set()
  );

  // Reset selection when the watchlist changes (new tickers array reference)
  const prevTickersRef = useRef(tickers);

  useEffect(() => {
    if (prevTickersRef.current !== tickers) {
      setSelectedTickers(new Set());
      prevTickersRef.current = tickers;
    }
  }, [tickers]);

  // Empty selection = all tickers selected
  const allSelected = selectedTickers.size === 0;

  const toggleTicker = useCallback((ticker: string) => {
    setSelectedTickers((prev) => {
      const next = new Set(prev);

      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }

      return next;
    });
  }, []);

  const activeTickers = useMemo(() => {
    if (allSelected) {
      return tickers.map((t) => t.ticker);
    }

    return Array.from(selectedTickers);
  }, [allSelected, tickers, selectedTickers]);

  return { allSelected, selectedTickers, activeTickers, toggleTicker };
}
